// src/components/foam/FoamSupport.js
//
// WARRANTY screen (`/boards/:sku/warranty`) — Nigel, 2026-06-05 (split from
// the combined Warranty & Support screen; Support is now its own Viento
// chat screen, FoamChat.js).
//
// Warranty status is computed from this board's registration date (1-year
// limited) and the policy text is pulled LIVE from
// waka.surf/pages/warranty-policy via getWarrantyPolicy — the website is the
// source of truth; never hardcode policy copy here.

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { FPage, FHeader, FLabel, FHeadline, FBrandText } from './FoamPrimitives';

const WARRANTY_MONTHS = 12; // 1-year limited warranty (waka.surf policy)

const FoamWarranty = ({ product, registration, onBack }) => {
  // ── Warranty status from the registration record ──────────────────────────
  const regDate = registration?.registrationDate?.toDate
    ? registration.registrationDate.toDate()
    : registration?.registrationDate
      ? new Date(registration.registrationDate)
      : null;
  const validRegDate = regDate && !isNaN(regDate.getTime()) ? regDate : null;
  const expiry = validRegDate ? new Date(validRegDate.getTime()) : null;
  if (expiry) expiry.setMonth(expiry.getMonth() + WARRANTY_MONTHS);
  const monthsLeft = expiry
    ? Math.max(0, Math.floor((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44)))
    : null;
  const active = expiry ? expiry.getTime() > Date.now() : false;

  // ── Policy text from the website (source of truth) ────────────────────────
  const [policy, setPolicy] = useState({ state: 'loading', text: null, sourceUrl: 'https://waka.surf/pages/warranty-policy' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await httpsCallable(functions, 'getWarrantyPolicy')();
        if (cancelled) return;
        const { text, sourceUrl } = res.data || {};
        setPolicy((p) => ({ state: text ? 'ready' : 'error', text, sourceUrl: sourceUrl || p.sourceUrl }));
      } catch (e) {
        console.error('Warranty policy fetch failed:', e);
        if (!cancelled) setPolicy((p) => ({ ...p, state: 'error' }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <FPage>
      <FHeader right={
        <button type="button" onClick={onBack} className="font-sans text-[14px] text-foam-ink">
          Back
        </button>
      } />

      <FLabel>Warranty</FLabel>

      <FHeadline size={50} lineHeight={0.92}>
        We've got<br />your <FHeadline.Accent>back.</FHeadline.Accent>
      </FHeadline>

      {product?.name && (
        <div className="font-mono text-[12px] tracking-[0.1em] uppercase text-foam-ink-2">
          <FBrandText size={13} color="var(--foam-ink)">{product.name}</FBrandText>
          {product?.caption ? ` · ${product.caption}` : ''}
        </div>
      )}

      <div className="flex flex-col" style={{ border: '1.5px solid var(--foam-line)' }}>
        <div className="p-[16px]" style={{ borderBottom: '1px solid var(--foam-hair)' }}>
          <div className="flex items-center justify-between">
            <FLabel>Status</FLabel>
            <span
              className="font-mono text-[12px] uppercase font-medium px-[10px] py-[4px]"
              style={{
                background: active ? 'var(--foam-teal)' : 'var(--foam-paper-2)',
                color: active ? 'var(--foam-paper)' : 'var(--foam-ink-2)',
                letterSpacing: '0.1em',
                borderRadius: 999,
              }}
            >
              {validRegDate ? (active ? `Active · ${monthsLeft} mo left` : 'Expired') : 'Unknown'}
            </span>
          </div>
          {validRegDate && (
            <div className="text-[12px] text-foam-ink-2 mt-[6px]">
              1-year limited warranty from registration
              ({validRegDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              {expiry ? ` — ${expiry.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}).
            </div>
          )}
        </div>

        <div className="p-[16px]">
          {policy.state === 'loading' && (
            <div className="font-mono text-[12px] tracking-[0.14em] uppercase text-foam-ink-2">
              ● Loading policy from waka.surf…
            </div>
          )}
          {policy.state === 'ready' && (
            <div className="text-[14px] leading-[1.55] text-foam-ink-2" style={{ whiteSpace: 'pre-line' }}>
              {policy.text}
            </div>
          )}
          {policy.state === 'error' && (
            <div className="text-[14px] leading-[1.5] text-foam-ink-2">
              Couldn't load the policy right now — read it on the website below.
            </div>
          )}
          <a
            href={policy.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="block mt-[12px] font-mono text-[12px] tracking-[0.08em] uppercase underline text-foam-ink"
          >
            Full policy on waka.surf →
          </a>
        </div>
      </div>
    </FPage>
  );
};

export default FoamWarranty;
